/**
 * Tutorial Stalemate Handler
 * 
 * This module contains tutorial-specific logic for handling stalemate situations.
 * It keeps the core game engine clean by moving tutorial business logic out of GameStateManager.
 */

import { isTutorialSolo } from './TutorialSpecs';
import { GameState } from '../../services/zodiacEngine/types';

export interface StalemateEventData {
  constellation: string;
  tutorialIndex: number;
  nextPlayerIsLight: boolean;
  gameState: GameState;
  message: string;
}

/**
 * Determines if a stalemate should be treated as a tutorial loss
 * @param eventData Stalemate event data from the engine
 * @returns true if this should trigger a tutorial loss, false otherwise
 */
export function shouldStalemateTriggerLoss(eventData: StalemateEventData): boolean {
  const { constellation, tutorialIndex } = eventData;
  
  // Check if this is a solo tutorial - solo tutorials don't trigger loss on stalemate
  const isSolo = isTutorialSolo(constellation, tutorialIndex);
  if (isSolo) {
    return false;
  }
  
  // Check for specific tutorials that should override stalemate with loss conditions
  const shouldPreventStalemate = shouldPreventStalemateForSpecificTutorial(constellation, tutorialIndex);
  if (shouldPreventStalemate) {
    return false;
  }
  
  // For all other non-solo tutorials, stalemate should trigger loss
  return true;
}

/**
 * Checks if a specific tutorial should prevent stalemate in favor of custom loss conditions
 * @param constellation Tutorial constellation
 * @param tutorialIndex Tutorial index (0-based)
 * @returns true if stalemate should be prevented for custom loss detection
 */
function shouldPreventStalemateForSpecificTutorial(constellation: string, tutorialIndex: number): boolean {
  // Bear tutorials with specific loss conditions that should override stalemate
  if (constellation === "Bear") {
    switch (tutorialIndex) {
      case 2: // Bear Tutorial 3 (Predator): Loss when Dark Goat reaches row 0
      case 3: // Bear Tutorial 4 (Race defence): Loss when Dark Crane escapes/reaches row 0
      // Note: Bear Tutorial 5 (index 4) is NOT here because stalemate should trigger loss
      // The tutorial has capture-based win conditions, so if player can't move, they've failed
        return true;
    }
  }
  
  // Add more constellation-specific rules here as needed
  // Example:
  // if (constellation === "Crane") {
  //   switch (tutorialIndex) {
  //     case 3: // Crane Tutorial 4: Custom loss condition
  //       return true;
  //   }
  // }
  
  return false;
}

/**
 * Gets a user-friendly message for tutorial stalemate loss
 * @param eventData Stalemate event data
 * @returns Formatted loss message
 */
export function getStalemateLossMessage(eventData: StalemateEventData): string {
  const { nextPlayerIsLight } = eventData;
  const playerType = nextPlayerIsLight ? "Light" : "Dark";
  
  return `Tutorial Failed! ${playerType} player has no legal moves available - this counts as a loss in tutorials!`;
}